import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { ApSummary, EstimateResult, Floor, ReferencePoint } from '@wrp/shared';
import type { Point } from '../format';
import {
  apIcon,
  buildGrid,
  clampToFloor,
  estimateIcon,
  estimateTooltip,
  floorBounds,
  fromLatLng,
  groundTruthIcon,
  referencePointIcon,
  roundPoint,
  sampleIcon,
  toLatLng,
} from '../mapUtils';
import { IconButton } from './Icon';

export interface MapSample {
  id: string;
  estimate: Point;
  /** Operator-calibrated actual position, if any. */
  actual: Point | null;
}

export interface FloorMapProps {
  floor: Floor | null;
  referencePoints: ReferencePoint[];
  aps?: ApSummary[];
  fingerprintCountByRefPoint?: Record<string, number>;
  estimate?: EstimateResult | null;
  /** Estimated positions of individual reports (evaluation mode). */
  samples?: MapSample[];
  selectedSampleId?: string | null;
  onSampleClick?: (id: string) => void;
  /** Extra marker for a manually entered "actual" position (evaluation mode). */
  actualPoint?: Point | null;
  /** When set, the actual marker can be dragged. */
  onActualMove?: (point: Point) => void;
  selectedReferencePointId?: string | null;
  selectedApBssid?: string | null;
  /** Clicking empty map space. Coordinates are floor metres. */
  onMapClick?: (point: Point) => void;
  onReferencePointClick?: (rp: ReferencePoint) => void;
  /** Fired after a reference-point marker is dragged to a new position. */
  onReferencePointMove?: (rp: ReferencePoint, point: Point) => void;
  onApClick?: (ap: ApSummary) => void;
  draggableReferencePoints?: boolean;
  /** Banner shown over the map to explain the current click mode. */
  hint?: string | null;
  /** Crosshair cursor while a click will place something. */
  picking?: boolean;
  /** Buttons overlaid on the top-left corner of the map. */
  tools?: ReactNode;
}

const NO_APS: ApSummary[] = [];
const NO_SAMPLES: MapSample[] = [];
const NO_COUNTS: Record<string, number> = {};
const FIT_OPTIONS: L.FitBoundsOptions = { padding: [10, 10], animate: false };

export function FloorMap({
  floor,
  referencePoints,
  aps = NO_APS,
  fingerprintCountByRefPoint = NO_COUNTS,
  estimate = null,
  samples = NO_SAMPLES,
  selectedSampleId = null,
  onSampleClick,
  actualPoint = null,
  onActualMove,
  selectedReferencePointId = null,
  selectedApBssid = null,
  onMapClick,
  onReferencePointClick,
  onReferencePointMove,
  onApClick,
  draggableReferencePoints = false,
  hint = null,
  picking = false,
  tools = null,
}: FloorMapProps) {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const [mapReady, setMapReady] = useState(0);
  // Pan/zoom is off by default so the plan stays put while the operator clicks around.
  const [navEnabled, setNavEnabled] = useState(false);
  const mapRef = useRef<L.Map | null>(null);
  const baseLayerRef = useRef<L.Layer | null>(null);
  const overlayRef = useRef<L.LayerGroup | null>(null);
  const boundsRef = useRef<L.LatLngBounds | null>(null);
  const navRef = useRef(navEnabled);
  navRef.current = navEnabled;

  const handlersRef = useRef({ onMapClick, onReferencePointClick, onReferencePointMove, onApClick, onSampleClick, onActualMove });
  handlersRef.current = { onMapClick, onReferencePointClick, onReferencePointMove, onApClick, onSampleClick, onActualMove };

  // One-shot click animation: the markers are rebuilt on every change, so the id to
  // animate is parked in a ref and consumed by the next rebuild.
  const pendingPulseRef = useRef<string | null>(null);
  const [pulseTick, setPulseTick] = useState(0);
  const doneRef = useRef<{ floorId: string; ids: Set<string> } | null>(null);

  const floorId = floor?.id ?? null;
  useEffect(() => {
    setNavEnabled(false);
  }, [floorId]);

  useEffect(() => {
    if (selectedReferencePointId) pendingPulseRef.current = selectedReferencePointId;
  }, [selectedReferencePointId]);

  const fit = () => {
    const map = mapRef.current;
    const bounds = boundsRef.current;
    if (map && bounds) map.fitBounds(bounds, FIT_OPTIONS);
  };

  // Create the Leaflet map as soon as the container element exists. The
  // container is mounted/unmounted with the floor, so this is a callback-ref
  // driven effect rather than a mount-once effect.
  useEffect(() => {
    if (!container) return;
    const map = L.map(container, {
      crs: L.CRS.Simple,
      minZoom: -6,
      maxZoom: 8,
      zoomSnap: 0,
      zoomDelta: 0.5,
      attributionControl: false,
      zoomControl: false,
    });
    map.setView(L.latLng(0, 0), 0);
    map.on('click', (event: L.LeafletMouseEvent) => {
      handlersRef.current.onMapClick?.(fromLatLng(event.latlng));
    });
    mapRef.current = map;
    overlayRef.current = L.layerGroup().addTo(map);
    setMapReady((n) => n + 1);

    const observer = new ResizeObserver(() => {
      map.invalidateSize();
      if (!navRef.current) fit();
    });
    observer.observe(container);

    return () => {
      observer.disconnect();
      map.remove();
      mapRef.current = null;
      overlayRef.current = null;
      baseLayerRef.current = null;
    };
  }, [container]);

  // Enable/disable user navigation. Turning it off snaps back to the whole plan.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const handlers = [
      map.dragging,
      map.scrollWheelZoom,
      map.doubleClickZoom,
      map.touchZoom,
      map.boxZoom,
      map.keyboard,
    ];
    for (const handler of handlers) {
      if (navEnabled) handler.enable();
      else handler.disable();
    }
    if (!navEnabled) fit();
  }, [navEnabled, mapReady]);

  // Base layer: floor-plan image when available, otherwise a metric grid.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (baseLayerRef.current) {
      map.removeLayer(baseLayerRef.current);
      baseLayerRef.current = null;
    }
    if (!floor) {
      boundsRef.current = null;
      return;
    }
    const bounds = floorBounds(floor);
    const base = L.layerGroup();
    if (floor.image_path) {
      L.imageOverlay(floor.image_path, bounds, { opacity: 1, interactive: false }).addTo(base);
    }
    buildGrid(floor).addTo(base);
    base.addTo(map);
    baseLayerRef.current = base;
    boundsRef.current = bounds;
    map.setMaxBounds(bounds.pad(1));
    map.invalidateSize();
    fit();
  }, [floor, mapReady]);

  // Markers for reference points, APs and the latest estimate.
  useEffect(() => {
    const map = mapRef.current;
    const overlay = overlayRef.current;
    if (!map || !overlay || !floor) {
      overlayRef.current?.clearLayers();
      return;
    }
    overlay.clearLayers();

    const pulseId = pendingPulseRef.current;
    pendingPulseRef.current = null;
    const doneIds = new Set(
      referencePoints.filter((rp) => rp.floor_id === floor.id && (fingerprintCountByRefPoint[rp.id] ?? 0) > 0).map((rp) => rp.id),
    );
    const previousDone = doneRef.current?.floorId === floor.id ? doneRef.current.ids : null;
    doneRef.current = { floorId: floor.id, ids: doneIds };

    for (const rp of referencePoints) {
      if (rp.floor_id !== floor.id) continue;
      const marker = L.marker(toLatLng(rp), {
        icon: referencePointIcon(rp, fingerprintCountByRefPoint[rp.id] ?? 0, rp.id === selectedReferencePointId, {
          pulse: rp.id === pulseId,
          justDone: previousDone !== null && doneIds.has(rp.id) && !previousDone.has(rp.id),
        }),
        draggable: draggableReferencePoints,
        keyboard: true,
        title: `${rp.label} (${rp.x.toFixed(2)}, ${rp.y.toFixed(2)} m)`,
      });
      marker.on('click', (event: L.LeafletMouseEvent) => {
        L.DomEvent.stopPropagation(event);
        pendingPulseRef.current = rp.id;
        setPulseTick((n) => n + 1);
        handlersRef.current.onReferencePointClick?.(rp);
      });
      marker.on('dragend', () => {
        const next = roundPoint(clampToFloor(floor, fromLatLng(marker.getLatLng())));
        handlersRef.current.onReferencePointMove?.(rp, next);
      });
      marker.addTo(overlay);
    }

    for (const ap of aps) {
      if (ap.x === null || ap.y === null) continue;
      if (ap.floor_id && ap.floor_id !== floor.id) continue;
      const marker = L.marker(L.latLng(ap.y, ap.x), {
        icon: apIcon(ap, ap.bssid === selectedApBssid),
        title: `${ap.ssid ?? ap.bssid} · ${ap.bssid} · avg ${ap.avg_rssi} dBm`,
      });
      marker.on('click', (event: L.LeafletMouseEvent) => {
        L.DomEvent.stopPropagation(event);
        handlersRef.current.onApClick?.(ap);
      });
      marker.addTo(overlay);
    }

    for (const sample of samples) {
      if (sample.actual) {
        L.polyline([toLatLng(sample.estimate), toLatLng(sample.actual)], {
          color: '#ff5d5d',
          weight: 1.5,
          dashArray: '4 3',
          opacity: sample.id === selectedSampleId ? 0.9 : 0.4,
          interactive: false,
        }).addTo(overlay);
        // The selected sample's actual position is drawn (and draggable) via actualPoint.
        if (sample.id !== selectedSampleId) {
          const truth = L.marker(toLatLng(sample.actual), { icon: groundTruthIcon(true) });
          truth.on('click', (event: L.LeafletMouseEvent) => {
            L.DomEvent.stopPropagation(event);
            pendingPulseRef.current = sample.id;
            setPulseTick((n) => n + 1);
            handlersRef.current.onSampleClick?.(sample.id);
          });
          truth.addTo(overlay);
        }
      }
      const marker = L.marker(toLatLng(sample.estimate), {
        icon: sampleIcon({
          calibrated: sample.actual !== null,
          selected: sample.id === selectedSampleId,
          pulse: sample.id === pulseId,
        }),
        zIndexOffset: sample.id === selectedSampleId ? 500 : 0,
      });
      marker.on('click', (event: L.LeafletMouseEvent) => {
        L.DomEvent.stopPropagation(event);
        pendingPulseRef.current = sample.id;
        setPulseTick((n) => n + 1);
        handlersRef.current.onSampleClick?.(sample.id);
      });
      marker.addTo(overlay);
    }

    if (estimate?.position && (!estimate.floor_id || estimate.floor_id === floor.id)) {
      const pos = estimate.position;
      const marker = L.marker(L.latLng(pos.y, pos.x), { icon: estimateIcon('estimate') });
      marker.bindTooltip(estimateTooltip(estimate), { direction: 'top', offset: L.point(0, -12) });
      marker.addTo(overlay);

      for (const neighbor of estimate.neighbors) {
        L.circleMarker(L.latLng(neighbor.y, neighbor.x), {
          radius: 5,
          color: '#f0b429',
          weight: 2,
          fillOpacity: 0.3,
          interactive: false,
        }).addTo(overlay);
        L.polyline([L.latLng(pos.y, pos.x), L.latLng(neighbor.y, neighbor.x)], {
          color: '#f0b429',
          weight: 1,
          dashArray: '3 4',
          opacity: 0.6,
          interactive: false,
        }).addTo(overlay);
      }

      const truth = estimate.ground_truth;
      if (truth) {
        L.marker(L.latLng(truth.y, truth.x), { icon: groundTruthIcon() }).addTo(overlay);
        L.polyline([L.latLng(pos.y, pos.x), L.latLng(truth.y, truth.x)], {
          color: '#ff5d5d',
          weight: 2,
          dashArray: '6 4',
          interactive: false,
        })
          .bindTooltip(
            typeof estimate.error_m === 'number' ? `error ${estimate.error_m.toFixed(2)} m` : 'error',
            { permanent: false },
          )
          .addTo(overlay);
      }
    }

    if (actualPoint && !estimate?.ground_truth) {
      const marker = L.marker(L.latLng(actualPoint.y, actualPoint.x), {
        icon: groundTruthIcon(),
        draggable: Boolean(handlersRef.current.onActualMove),
        zIndexOffset: 1000,
      });
      marker.on('dragend', () => {
        handlersRef.current.onActualMove?.(roundPoint(clampToFloor(floor, fromLatLng(marker.getLatLng()))));
      });
      marker.addTo(overlay);
    }
  }, [
    floor,
    mapReady,
    referencePoints,
    aps,
    fingerprintCountByRefPoint,
    estimate,
    samples,
    selectedSampleId,
    actualPoint,
    selectedReferencePointId,
    selectedApBssid,
    draggableReferencePoints,
    pulseTick,
  ]);

  if (!floor) {
    return (
      <div className="map-empty">
        <p>No floor selected.</p>
        <p className="muted">Add a floor to start placing reference points.</p>
      </div>
    );
  }

  const zoomBy = (delta: number) => mapRef.current?.setZoom((mapRef.current?.getZoom() ?? 0) + delta);

  return (
    <div className="map-shell">
      <div ref={setContainer} className={`map-canvas${picking ? ' is-picking' : ''}`} />
      {hint ? <div className="map-hint">{hint}</div> : null}
      {tools ? <div className="map-tools">{tools}</div> : null}
      <div className="map-nav">
        {navEnabled ? (
          <>
            <IconButton icon="zoomIn" label="Zoom in" size={15} onClick={() => zoomBy(0.5)} />
            <IconButton icon="zoomOut" label="Zoom out" size={15} onClick={() => zoomBy(-0.5)} />
            <IconButton icon="fit" label="Fit plan" size={15} onClick={fit} />
          </>
        ) : null}
        <IconButton
          icon={navEnabled ? 'unlock' : 'lock'}
          label={navEnabled ? 'Pan and zoom enabled — click to lock' : 'Pan and zoom locked — click to enable'}
          active={navEnabled}
          size={15}
          onClick={() => setNavEnabled((v) => !v)}
        />
      </div>
      <div className="map-overlay">
        {referencePoints.length > 0 ? (
          <>
            <span className="legend legend--rp-done" title="Reference point with fingerprints">completed</span>
            <span className="legend legend--rp" title="Reference point without fingerprints">pending</span>
          </>
        ) : null}
        {aps.length > 0 ? <span className="legend legend--ap">AP</span> : null}
        {estimate || samples.length > 0 ? <span className="legend legend--estimate">estimate</span> : null}
        {actualPoint || estimate?.ground_truth || samples.some((s) => s.actual) ? <span className="legend legend--truth">actual</span> : null}
        <span className="map-overlay__size">
          {floor.width_m} × {floor.height_m} m
        </span>
      </div>
    </div>
  );
}
